import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Slider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { useTeamLimits } from '../../hooks/useTeamLimits';
import { EstimatedCostChip } from '../common/EstimatedCostChip';
import { StandardTable } from '../common/UI/StandardTable';
import SupportDocumentTable from './SupportDocumentTable';
import type { LoadingStates, SupportDoc } from './types';
import { getDropzoneBoxStyle, getLoadingKey, toDate } from './utils';

interface AdHocDocGenerationSectionProps {
  tenderId: string;
  usedCount?: number;
  tenderForms: SupportDoc[];
  loadingStates: LoadingStates;
  isDragging: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileUpload: (file: File, isStrictForm?: boolean) => void;
  uploadValidationWarning?: string | undefined;
  processingFiles: string[];
  onGenerateForm: (
    doc: SupportDoc,
    confidenceThreshold?: number,
    flagUncertainAnswers?: boolean
  ) => void;
  onDownloadInput: (doc: SupportDoc) => void;
  onDownloadFilledForm: (doc: SupportDoc) => void;
  onDeleteForm: (doc: SupportDoc) => void;
  onResetForm: (doc: SupportDoc) => void;
  isDraftGenerated: boolean;
  onResetService?: () => void;
}

const AdHocDocGenerationSection: React.FC<AdHocDocGenerationSectionProps> = ({
  tenderId,
  usedCount = 0,
  tenderForms,
  loadingStates,
  isDragging,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop: _onDrop,
  onFileUpload,
  uploadValidationWarning,
  processingFiles,
  onGenerateForm,
  onDownloadInput,
  onDownloadFilledForm,
  onDeleteForm,
  onResetForm,
  isDraftGenerated,
  onResetService,
}) => {
  const theme = useTheme();
  const { pricingSettings } = usePricing();

  const [pendingUploads, setPendingUploads] = useState<{ file: File; isStrict: boolean }[]>([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(() => {
    const saved = localStorage.getItem('adhoc_confidenceThreshold');
    return saved !== null ? parseInt(saved, 10) : 75;
  });
  const [flagUncertainAnswers, setFlagUncertainAnswers] = useState<boolean>(() => {
    const saved = localStorage.getItem('adhoc_flagUncertainAnswers');
    return saved !== null ? saved === 'true' : true;
  });

  const handleConfidenceChange = (newValue: number) => {
    setConfidenceThreshold(newValue);
    localStorage.setItem('adhoc_confidenceThreshold', newValue.toString());
  };

  const handleFlagChange = (newValue: boolean) => {
    setFlagUncertainAnswers(newValue);
    localStorage.setItem('adhoc_flagUncertainAnswers', newValue.toString());
  };

  const handleInterceptUpload = (files: File[]) => {
    const toReview: { file: File; isStrict: boolean }[] = [];
    for (const file of files) {
      const isPdfOrDocx =
        file.name.toLowerCase().endsWith('.pdf') || file.name.toLowerCase().endsWith('.docx');
      if (isPdfOrDocx) {
        toReview.push({ file, isStrict: false });
      } else {
        onFileUpload(file, false);
      }
    }
    if (toReview.length > 0) {
      setPendingUploads(toReview);
    }
  };

  const handleConfirmUploads = () => {
    for (const { file, isStrict } of pendingUploads) {
      onFileUpload(file, isStrict);
    }
    setPendingUploads([]);
  };

  const handleCancelUploads = () => {
    setPendingUploads([]);
  };

  const togglePendingUploadStrict = (index: number) => {
    setPendingUploads((prev) => {
      const newUploads = [...prev];
      if (newUploads[index]) {
        newUploads[index].isStrict = !newUploads[index].isStrict;
      }
      return newUploads;
    });
  };

  const { checkLimit } = useTeamLimits();
  const {
    remaining: remainingQuota,
    isLimitReached,
    isUnlimited,
  } = checkLimit('adHocDocs', usedCount);
  const hasQuota = !isLimitReached;

  // Global Reset Cooldown Calculation
  let globalResetDisabled = false;
  let maxGlobalCooldownMins = 0;

  for (const doc of tenderForms) {
    const isProcessing =
      doc.generationStatus === 'processing' || doc.generationStatus === 'initializing';

    if (isProcessing) {
      const refDate = doc.startedAt || doc.updatedAt || doc.lastModified;
      if (refDate) {
        const startedAt = toDate(refDate);
        const now = new Date();
        const diff = now.getTime() - startedAt.getTime();
        const cooldownMs = 15 * 60 * 1000;
        if (diff < cooldownMs) {
          globalResetDisabled = true;
          const minsLeft = Math.ceil((cooldownMs - diff) / 60000);
          if (minsLeft > maxGlobalCooldownMins) {
            maxGlobalCooldownMins = minsLeft;
          }
        }
      } else {
        // If processing but no date, assume active cooldown
        globalResetDisabled = true;
        maxGlobalCooldownMins = 15;
      }
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Left Column: Instructions and Estimate */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography
              variant="body1"
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
              component="div"
            >
              <strong>Workflow:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                <li>
                  <strong>Generate Draft first:</strong> You must generate your draft proposal
                  before generating returnable forms. This ensures the forms are tailored perfectly
                  to match the exact promises and context of your proposal.
                </li>
                <li>
                  <strong>Context Source:</strong> The AI will use the latest draft uploaded to the{' '}
                  <em>Review & Edit</em> tab. If no manual upload exists, it will fall back to using
                  the current state of your generated sections.
                </li>
                <li>
                  <strong>Generate Documents:</strong> Upload forms, spreadsheets, templates,
                  addendums, or other documents to have them filled in. Supports text, documents,
                  and spreadsheets.
                </li>
              </ul>
            </Typography>
            {hasQuota && (
              <Box sx={{ mt: 2 }}>
                <Tooltip
                  title="Exact billing depends on the length and complexity of the tender documents and provided context."
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <EstimatedCostChip
                    estimate={
                      pricingSettings?.estimates?.adhoc_doc_generation || 'Est. $15.00 – $50.00'
                    }
                    sx={{ fontSize: '0.875rem' }}
                  />
                </Tooltip>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Column: Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={getDropzoneBoxStyle(theme, isDragging)}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDragLeave(e);
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                handleInterceptUpload(Array.from(e.dataTransfer.files));
              }
            }}
          >
            <input
              type="file"
              id="form-upload"
              multiple
              accept=".pdf,.docx,.txt,.md,.csv,.xls,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleInterceptUpload(Array.from(e.target.files));
                }
                e.target.value = '';
              }}
            />
            <label htmlFor="form-upload" style={{ width: '100%', cursor: 'pointer' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {loadingStates[getLoadingKey(tenderId, 'upload', 'form')] ? (
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                ) : (
                  <UploadFileIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                )}
                <Typography variant="body2" color="textSecondary" align="center">
                  {loadingStates[getLoadingKey(tenderId, 'upload', 'form')]
                    ? 'Uploading...'
                    : 'Drag & Drop documents here (PDF, DOCX, TXT, CSV, XLS, XLSX, MD - Max 5MB)'}
                </Typography>
              </Box>
            </label>
          </Box>
          <Box sx={{ mt: 3, px: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Form fill confidence
                  </Typography>
                  <Tooltip
                    title={
                      <Typography variant="body2">
                        When the AI generates an answer, it assigns itself a confidence score from
                        10-90%. If its confidence is below this threshold, it will insert a{' '}
                        <strong>[PLACEHOLDER]</strong> block into the document instead of guessing,
                        ensuring you can manually review uncertain fields.
                      </Typography>
                    }
                    placement="top"
                    arrow
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: 'text.secondary', cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Box>
                <Slider
                  value={confidenceThreshold}
                  onChange={(_e, newValue) => handleConfidenceChange(newValue as number)}
                  aria-labelledby="confidence-slider"
                  valueLabelDisplay="auto"
                  step={10}
                  marks
                  min={10}
                  max={90}
                  sx={{ color: theme.palette.bidlogiq.camel }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Fill but flag
                  </Typography>
                  <Tooltip
                    title={
                      <Typography variant="body2">
                        If enabled, the AI will keep its best guess but prefix it with{' '}
                        <strong>[REVIEW_FLAG]</strong>. If disabled, it will completely remove the
                        uncertain answer and insert a <strong>[PLACEHOLDER]</strong> block instead.
                      </Typography>
                    }
                    placement="top"
                    arrow
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: 'text.secondary', cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Box>
                <Switch
                  size="small"
                  checked={flagUncertainAnswers}
                  onChange={(e) => handleFlagChange(e.target.checked)}
                  sx={{
                    ml: -1,
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: theme.palette.bidlogiq.brick,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: theme.palette.bidlogiq.brick,
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
          {/* Upload Validation Warning for Forms */}
          {uploadValidationWarning && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                fontSize: '1rem',
                backgroundColor: 'bidlogiq.taupe',
                color: 'bidlogiq.black',
                '& .MuiAlert-icon': { color: 'bidlogiq.black' },
                '& .MuiAlert-message': { fontSize: '1rem' },
              }}
            >
              {uploadValidationWarning}
            </Alert>
          )}
          {/* Processing Files List for Forms */}
          {processingFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Processing Uploads:
              </Typography>
              <StandardTable
                columns={[
                  {
                    id: 'fileName',
                    label: 'File Name',
                    render: (fileName) => fileName,
                  },
                  {
                    id: 'progress',
                    label: '',
                    align: 'right',
                    render: () => <CircularProgress size={16} />,
                  },
                ]}
                data={processingFiles}
                keyExtractor={(fileName) => fileName}
                size="small"
                stickyHeader={false}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Full Width Table Section */}
      <Box sx={{ width: '100%' }}>
        <SupportDocumentTable
          title={
            <>
              Uploaded docs{' '}
              {isUnlimited ? '' : remainingQuota !== undefined && `(${remainingQuota} left)`}
            </>
          }
          documents={tenderForms}
          emptyMessage="No documents uploaded yet."
          renderNameExtras={(doc) => (
            <>
              {doc.tooLargeToProcess && (
                <Chip
                  label="Too large to process"
                  color="error"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: '20px',
                    mr: 0.5,
                  }}
                />
              )}
              {doc.isStrictForm && (
                <Chip
                  label="FORM"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: '20px',
                    bgcolor: 'greyContrast',
                    color: 'primary.contrastText',
                  }}
                />
              )}
              {doc.type === 'auto-imported-addenda' && (
                <Chip
                  label="New Addenda"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: 'greyContrast',
                    color: 'primary.contrastText',
                  }}
                />
              )}
            </>
          )}
          renderUnderName={(doc) => {
            const isFailed = doc.conversionStatus === 'failed';
            return (
              (doc.storageUri || doc.gcsPath || doc.downloadURL || doc.downloadUrl) && (
                <Typography
                  variant="caption"
                  sx={{
                    cursor: isFailed ? 'not-allowed' : 'pointer',
                    color: isFailed ? 'text.disabled' : 'primary.main',
                    textDecoration: isFailed ? 'none' : 'underline',
                    display: 'block',
                    mt: 0.5,
                    '&:hover': {
                      color: isFailed ? 'text.disabled' : 'primary.dark',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isFailed) {
                      onDownloadInput(doc);
                    }
                  }}
                >
                  {isFailed ? 'Conversion Failed' : 'Download Original'}
                </Typography>
              )
            );
          }}
          renderActions={(doc) => {
            const isLocalLoading =
              !!loadingStates[getLoadingKey(tenderId, 'generate', `form_${doc.originalFilename}`)];
            const isProcessing =
              isLocalLoading ||
              doc.generationStatus === 'processing' ||
              doc.generationStatus === 'initializing';
            const isSuccess =
              doc.generationStatus === 'success' ||
              doc.generationStatus?.toLowerCase().includes('complete');

            const isError = doc.generationStatus === 'error' || doc.generationStatus === 'failed';

            let canReset = false;
            const refDate = doc.startedAt || doc.updatedAt || doc.lastModified;
            if (doc.generationStatus === 'initializing' || isError) {
              canReset = true;
            } else if (isProcessing && refDate) {
              const startedAt = toDate(refDate);
              const now = new Date();
              const diff = now.getTime() - startedAt.getTime();
              const cooldownMs = 15 * 60 * 1000;
              if (diff > cooldownMs) {
                canReset = true;
              }
            } else if (isProcessing) {
              canReset = false;
            }

            return (
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}
              >
                <Tooltip
                  title={
                    !isDraftGenerated
                      ? 'Generate a draft to enable generation'
                      : !hasQuota
                        ? 'Monthly limit reached'
                        : doc.conversionStatus === 'failed'
                          ? 'Conversion failed. Cannot generate.'
                          : doc.conversionStatus !== undefined && doc.conversionStatus !== 'success'
                            ? 'File is processing... please wait'
                            : doc.isStrictForm
                              ? 'Fill Form'
                              : 'Generate filled form'
                  }
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <span>
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        onGenerateForm(doc, confidenceThreshold, flagUncertainAnswers);
                      }}
                      disabled={
                        (isProcessing && !isError) ||
                        !isDraftGenerated ||
                        !hasQuota ||
                        doc.conversionStatus === 'failed' ||
                        (!doc.isStrictForm &&
                          doc.conversionStatus !== undefined &&
                          doc.conversionStatus !== 'success')
                      }
                      variant="contained"
                      size="small"
                      sx={{
                        backgroundColor: isError
                          ? theme.palette.error.main
                          : theme.palette.bidlogiq.brick,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: isError
                            ? theme.palette.error.dark
                            : theme.palette.bidlogiq.brick,
                          opacity: 0.9,
                        },
                        '&.Mui-disabled': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.12)'
                              : theme.palette.grey[300],
                          color:
                            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'inherit',
                        },
                        fontSize: '0.875rem',
                        px: 1.5,
                        width: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        textTransform: 'none',
                      }}
                    >
                      {isProcessing && !isError ? (
                        <>
                          <CircularProgress size={14} color="inherit" variant="indeterminate" />
                          {doc.generationProgress
                            ? `${doc.generationProgress}%`
                            : doc.isStrictForm
                              ? 'Filling...'
                              : 'Generating...'}
                        </>
                      ) : isError ? (
                        'Retry'
                      ) : isSuccess ? (
                        doc.isStrictForm ? (
                          'Fill Form'
                        ) : (
                          'Regenerate'
                        )
                      ) : doc.isStrictForm ? (
                        'Fill Form'
                      ) : (
                        'Generate'
                      )}
                    </Button>
                  </span>
                </Tooltip>

                {isProcessing && !isError && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onResetForm(doc);
                    }}
                    disabled={!canReset}
                    sx={{
                      borderColor: 'text.error',
                      color: 'text.error',
                      fontSize: '0.7rem',
                      px: 1,
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Reset
                  </Button>
                )}
                <IconButton
                  onClick={() => onDownloadFilledForm(doc)}
                  disabled={
                    !(doc.filledVersionStorageUri || doc.filledVersionGcsPath) ||
                    !!loadingStates[
                      getLoadingKey(tenderId, 'download', `filled_form_${doc.originalFilename}`)
                    ]
                  }
                  title="Download filled form"
                  size="small"
                  sx={{
                    color:
                      doc.filledVersionStorageUri || doc.filledVersionGcsPath
                        ? theme.palette.bidlogiq.brick
                        : theme.palette.action.disabled,
                  }}
                >
                  {loadingStates[
                    getLoadingKey(tenderId, 'download', `filled_form_${doc.originalFilename}`)
                  ] ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
                <IconButton
                  aria-label="delete"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onDeleteForm(doc);
                  }}
                  disabled={
                    !!loadingStates[
                      `delete_form_${doc.originalFilename || doc.name || doc.filename || doc.id}`
                    ]
                  }
                  size="small"
                >
                  {loadingStates[
                    `delete_form_${doc.originalFilename || doc.name || doc.filename || doc.id}`
                  ] ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
              </Box>
            );
          }}
        />
        {tenderForms.length > 0 && onResetService && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={onResetService}
              disabled={globalResetDisabled}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              {globalResetDisabled ? `Reset Service (${maxGlobalCooldownMins}m)` : 'Reset Service'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Strict Form Dialog */}
      <Dialog
        open={pendingUploads.length > 0}
        onClose={handleCancelUploads}
        aria-labelledby="strict-form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="strict-form-dialog-title">Review Uploaded Files</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem' }}>
            <strong>What is a Strict Form?</strong>
            <br />
            Mark files as "Strict Forms" if they are response schedules or returnables where the
            exact template format must be preserved. The AI will strictly fill in the blanks without
            altering the layout. Leave disabled for general reference materials.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pendingUploads.map((item, index) => (
              <Box
                key={`${item.file.name}-${item.file.size}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ wordBreak: 'break-all', pr: 2 }}>
                  {item.file.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Typography
                    variant="body2"
                    color={item.isStrict ? 'text.primary' : 'text.secondary'}
                  >
                    Strict Form
                  </Typography>
                  <Switch
                    size="small"
                    checked={item.isStrict}
                    onChange={() => togglePendingUploadStrict(index)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.palette.bidlogiq.brick,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: theme.palette.bidlogiq.brick,
                      },
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCancelUploads} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmUploads} color="primary" variant="contained">
            Confirm & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdHocDocGenerationSection;
